<template>
  <div class="base-basic">
    <!-- 顶部导航栏 -->
    <div class="top-nav">
      <van-icon name="arrow-left" class="back-icon" @click="goBack" />
      <div class="nav-title">会议确认</div>
    </div>

    <!-- 内容区域 -->
    <div class="body-content">
      <!-- 待处理提示 -->
      <div v-if="!isCurrentProcessor" class="pending-notice">
        待当前处理人处理
      </div>

      <!-- 会议信息卡片 -->
      <MeetingInfoCard 
        :meeting-info="meetingInfo" 
        title="会议信息" 
        :show-notice="false"
      />

      <!-- 操作按钮区域 -->
      <div class="action-buttons">
        <van-button
          v-if="isPending" 
          type="primary" 
          size="large" 
          round 
          block 
          color="#145294"
          @click="handleConfirmAttend"
          :disabled="!isCurrentProcessor"
          class="confirm-btn"
        >
          确认参会
        </van-button>
        <van-button 
          v-if="isPending" 
          type="primary" 
          size="large" 
          round 
          block 
          color="#f5222d"
          @click="handleCannotAttend"
          :disabled="!isCurrentProcessor"
          class="cannot-attend-btn"
        >
          无法参会
        </van-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { Icon, Button, showToast, showDialog } from 'vant';
import MeetingInfoCard from '../components/MeetingInfoCard.vue';
import axios from '@/api/axios';

export default {
  name: 'MeetingConfirmDetail',
  components: {
    VanIcon: Icon,
    VanButton: Button,
    MeetingInfoCard
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const meetingInfo = ref(null);
    const isCurrentProcessor = ref(true);
    const isPending = ref(true);
    const isMounted = ref(true);

    // 返回上一页
    const goBack = () => {
      router.back();
    };

    // 获取会议详情
    const fetchMeetingDetail = async () => {
      try {
        const id = route.params.id;
        const response = await axios.get(`/meetings/${id}`);
        
        if (isMounted.value && response.data && response.data.success) {
          meetingInfo.value = response.data.data;
          checkIfCurrentProcessor();
          checkIsPending();
        } else if (isMounted.value) {
          showToast('获取会议信息失败');
          router.back();
        }
      } catch (error) {
        console.error('获取会议信息失败:', error);
        if (isMounted.value) {
          showToast('获取会议信息失败');
          router.back();
        }
      }
    };

    // 检查当前用户是否是处理人
    const checkIfCurrentProcessor = () => {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      if (userInfo && meetingInfo.value) {
        // 检查meetingInfo中是否有approve_id字段，并且与当前用户ID匹配
        isCurrentProcessor.value = userInfo.id === meetingInfo.value.approve_id;
      }
    };
    const checkIsPending = () => {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      if (userInfo && meetingInfo.value) {
        // 检查meetingInfo中是否有approve_id字段，并且与当前用户ID匹配
        isPending.value = meetingInfo.value.status === 'pending'
      }
    };

    // 处理确认参会
    const handleConfirmAttend = () => {
      showDialog({
        title: '确认参会',
        message: '您确定要确认参会吗？',
        confirmButtonText: '确定',
        cancelButtonText: '取消'
      }).then(async () => {
        try {
          // 调用确认参会接口
          await axios.post(`/api/meetings/${meetingInfo.value.id}/confirm`);
          if (isMounted.value) {
            showToast('确认成功');
            setTimeout(() => {
              if (isMounted.value) {
                router.push('/confirm');
              }
            }, 1500);
          }
        } catch (error) {
          console.error('确认参会失败:', error);
          if (isMounted.value) {
            showToast('确认失败');
          }
        }
      }).catch(() => {
        // 取消操作
      });
    };

    // 处理无法参会
    const handleCannotAttend = () => {
      showDialog({
        title: '无法参会',
        message: '您确定无法参会吗？',
        confirmButtonText: '确定',
        cancelButtonText: '取消'
      }).then(async () => {
        try {
          // 调用无法参会接口
          await axios.post(`/api/meetings/${meetingInfo.value.id}/cannot-attend`);
          if (isMounted.value) {
            showToast('操作成功');
            setTimeout(() => {
              if (isMounted.value) {
                router.push('/confirm');
              }
            }, 1500);
          }
        } catch (error) {
          console.error('无法参会操作失败:', error);
          if (isMounted.value) {
            showToast('操作失败');
          }
        }
      }).catch(() => {
        // 取消操作
      });
    };

    // 组件挂载时获取会议详情
    onMounted(() => {
      isMounted.value = true;
      fetchMeetingDetail();
    });

    // 组件卸载时清理资源
    onUnmounted(() => {
      isMounted.value = false;
    });

    return {
      goBack,
      meetingInfo,
      isCurrentProcessor,
      isPending,
      handleConfirmAttend,
      handleCannotAttend
    };
  }
};
</script>

<style scoped>
.base-basic {
  width: 100%;
  height: 100vh;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

/* 顶部导航栏 */
.top-nav {
  display: flex;
  align-items: center;
  height: 48px;
  background-color: #fff;
  border-bottom: 1px solid #eee;
  padding: 0 15px;
  position: relative;
}

.back-icon {
  font-size: 20px;
  color: #333;
}

.nav-title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

/* 内容区域 */
.body-content {
  flex: 1;
  padding: 20px 15px;
  overflow-y: auto;
}

/* 待处理提示 */
.pending-notice {
  background-color: #fff1f0;
  border: 1px solid #ffccc7;
  color: #f5222d;
  padding: 10px 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
}

/* 操作按钮区域 */
.action-buttons {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

/* 确认参会按钮 */
.confirm-btn {
  font-size: 16px;
  font-weight: 500;
  height: 48px;
}

/* 无法参会按钮 */
.cannot-attend-btn {
  font-size: 16px;
  font-weight: 500;
  height: 48px;
}
</style>